import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";

export interface TTSConfig {
  seed?: number | 'none';
  attempt?: number;
  language_code: string;
  apply_text_normalization: string;
  voice_settings: {
    stability: number;
    similarity_boost: number;
    style: number;
    speed: number;
    use_speaker_boost: boolean;
  };
}

export interface DevModeData {
  requestPayload?: Record<string, unknown>;
  responseData?: unknown;
  requestTimestamp?: string;
  responseTimestamp?: string;
  error?: string;
  ttsRequest?: {
    originalText?: string;
    normalizedText?: string;
    voiceId?: string;
    timestamp?: string;
    config?: TTSConfig;
  };
}

interface DevModePanelProps {
  data: DevModeData;
}

export function DevModePanel({ data }: DevModePanelProps) {
  const { t } = useTranslation('generator');
  const [isExpanded, setIsExpanded] = useState(false);
  const [comment, setComment] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopyAsJson = () => {
    const exportData = {
      comment: comment || undefined,
      request: {
        payload: data.requestPayload,
        timestamp: data.requestTimestamp,
      },
      response: {
        data: data.responseData,
        timestamp: data.responseTimestamp,
        error: data.error,
      },
      tts: data.ttsRequest ? {
        originalText: data.ttsRequest.originalText,
        normalizedText: data.ttsRequest.normalizedText,
        voiceId: data.ttsRequest.voiceId,
        timestamp: data.ttsRequest.timestamp,
        config: data.ttsRequest.config,
      } : undefined,
      exportedAt: new Date().toISOString(),
    };

    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
    setCopied(true);
    toast({
      title: t('devMode.copiedSuccess'),
      description: t('devMode.copiedJson'),
    });

    setTimeout(() => setCopied(false), 2000);
  };

  const hasData = data.requestPayload || data.responseData || data.error || data.ttsRequest;

  if (!hasData) {
    return (
      <div className="mt-4 p-3 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20">
        <p className="text-xs text-muted-foreground text-center">
          {t('devMode.noData')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-border bg-card overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <span className="text-sm font-medium text-foreground">
          {t('devMode.title')}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {t('devMode.request')}
              </h4>
              {data.requestTimestamp && (
                <span className="text-xs text-muted-foreground">
                  {new Date(data.requestTimestamp).toLocaleTimeString('pl-PL')}
                </span>
              )}
            </div>
            <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-auto max-h-48 whitespace-pre-wrap break-all font-mono">
              {data.requestPayload
                ? JSON.stringify(data.requestPayload, null, 2)
                : t('devMode.noDataShort')}
            </pre>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {t('devMode.response')}
              </h4>
              {data.responseTimestamp && (
                <span className="text-xs text-muted-foreground">
                  {new Date(data.responseTimestamp).toLocaleTimeString('pl-PL')}
                </span>
              )}
            </div>
            <pre className={`text-xs p-3 rounded-md overflow-auto max-h-48 whitespace-pre-wrap break-all font-mono ${
              data.error ? 'bg-destructive/10 text-destructive' : 'bg-muted/50'
            }`}>
              {data.error
                ? `ERROR: ${data.error}`
                : data.responseData
                  ? JSON.stringify(data.responseData, null, 2)
                  : t('devMode.noDataShort')}
            </pre>
          </div>

          {data.ttsRequest && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('devMode.ttsTitle')}
                </h4>
                {data.ttsRequest.timestamp && (
                  <span className="text-xs text-muted-foreground">
                    {new Date(data.ttsRequest.timestamp).toLocaleTimeString('pl-PL')}
                  </span>
                )}
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Voice ID: </span>
                    <span className="font-mono">{data.ttsRequest.voiceId}</span>
                  </div>
                  {data.ttsRequest.config && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Seed: </span>
                        <span className="font-mono">{data.ttsRequest.config.seed}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Attempt: </span>
                        <span className="font-mono">{data.ttsRequest.config.attempt || 1}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Language: </span>
                        <span className="font-mono">{data.ttsRequest.config.language_code}</span>
                      </div>
                    </>
                  )}
                </div>

                {data.ttsRequest.config?.voice_settings && (
                  <div className="p-2 rounded bg-muted/30 border border-border/50">
                    <span className="text-xs text-muted-foreground block mb-1">Voice Settings:</span>
                    <div className="grid grid-cols-3 gap-1 text-xs font-mono">
                      <span>stability: {data.ttsRequest.config.voice_settings.stability}</span>
                      <span>similarity: {data.ttsRequest.config.voice_settings.similarity_boost}</span>
                      <span>style: {data.ttsRequest.config.voice_settings.style}</span>
                      <span>speed: {data.ttsRequest.config.voice_settings.speed}</span>
                      <span>boost: {data.ttsRequest.config.voice_settings.use_speaker_boost ? 'on' : 'off'}</span>
                      <span>norm: {data.ttsRequest.config.apply_text_normalization}</span>
                    </div>
                  </div>
                )}

                <div>
                  <span className="text-xs text-muted-foreground block mb-1">{t('devMode.originalText')}</span>
                  <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-auto max-h-32 whitespace-pre-wrap break-all font-mono">
                    {data.ttsRequest.originalText || t('devMode.noDataShort')}
                  </pre>
                </div>

                <div>
                  <span className="text-xs text-muted-foreground block mb-1">{t('devMode.normalizedText')}</span>
                  <pre className="text-xs bg-primary/10 p-3 rounded-md overflow-auto max-h-32 whitespace-pre-wrap break-all font-mono border border-primary/20">
                    {data.ttsRequest.normalizedText || t('devMode.noDataShort')}
                  </pre>
                </div>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {t('devMode.comment')}
            </h4>
            <Textarea
              placeholder={t('devMode.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="text-sm resize-none"
              rows={3}
            />
          </div>

          <Button onClick={handleCopyAsJson} variant="outline" size="sm" className="w-full">
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4 text-green-500" />
                {t('devMode.copiedSuccess')}
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                {t('devMode.copyAsJson')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
